/* global axios */

import ApiClient from './ApiClient';

class CrmAPI extends ApiClient {
  constructor() {
    super('crm', { accountScoped: true });
  }

  getBoard({ pipelineId, query, assigneeId, teamId, status, from, to } = {}) {
    return axios.get(`${this.url}/board`, {
      params: {
        pipeline_id: pipelineId,
        q: query || undefined,
        assignee_id: assigneeId || undefined,
        team_id: teamId || undefined,
        status: status || undefined,
        from: from || undefined,
        to: to || undefined,
      },
    });
  }

  getDashboard(params = {}) {
    return axios.get(`${this.url}/dashboard`, { params });
  }

  getReports(params = {}) {
    return axios.get(`${this.url}/reports`, { params });
  }

  getConversationSummary(conversationId) {
    return axios.get(`${this.url}/conversation_summary`, {
      params: { conversation_id: conversationId },
    });
  }

  exportReports(params = {}) {
    return axios.get(`${this.url}/reports/export`, {
      params,
      responseType: 'blob',
    });
  }

  importDeals(file) {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${this.url}/deal_import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  getPipelines() {
    return axios.get(`${this.url}/pipelines`);
  }

  createPipeline(data) {
    return axios.post(`${this.url}/pipelines`, { pipeline: data });
  }

  updatePipeline(pipelineId, data) {
    return axios.patch(`${this.url}/pipelines/${pipelineId}`, {
      pipeline: data,
    });
  }

  deletePipeline(pipelineId) {
    return axios.delete(`${this.url}/pipelines/${pipelineId}`);
  }

  getStages(pipelineId) {
    return axios.get(`${this.url}/stages`, {
      params: { pipeline_id: pipelineId },
    });
  }

  createStage(data) {
    return axios.post(`${this.url}/stages`, { stage: data });
  }

  updateStage(stageId, data) {
    return axios.patch(`${this.url}/stages/${stageId}`, { stage: data });
  }

  deleteStage(stageId) {
    return axios.delete(`${this.url}/stages/${stageId}`);
  }

  getCommercialContacts(params = {}) {
    return axios.get(`${this.url}/contacts`, { params });
  }

  getCommercialContact(contactId) {
    return axios.get(`${this.url}/contacts/${contactId}`);
  }

  updateCommercialContact(contactId, data) {
    return axios.patch(`${this.url}/contacts/${contactId}`, { contact: data });
  }

  getDeals(params = {}) {
    return axios.get(`${this.url}/deals`, { params });
  }

  getDeal(dealId) {
    return axios.get(`${this.url}/deals/${dealId}`);
  }

  getNotes(params = {}) {
    return axios.get(`${this.url}/notes`, { params });
  }

  createNote(data) {
    return axios.post(`${this.url}/notes`, { note: data });
  }

  deleteNote(noteId) {
    return axios.delete(`${this.url}/notes/${noteId}`);
  }

  getProducts(params = {}) {
    return axios.get(`${this.url}/products`, { params });
  }

  createProduct(data) {
    return axios.post(`${this.url}/products`, { product: data });
  }

  updateProduct(productId, data) {
    return axios.patch(`${this.url}/products/${productId}`, { product: data });
  }

  deleteProduct(productId) {
    return axios.delete(`${this.url}/products/${productId}`);
  }

  getDealItems(dealId) {
    return axios.get(`${this.url}/deals/${dealId}/items`);
  }

  createDealItem(dealId, data) {
    return axios.post(`${this.url}/deals/${dealId}/items`, { item: data });
  }

  updateDealItem(dealId, itemId, data) {
    return axios.patch(`${this.url}/deals/${dealId}/items/${itemId}`, {
      item: data,
    });
  }

  deleteDealItem(dealId, itemId) {
    return axios.delete(`${this.url}/deals/${dealId}/items/${itemId}`);
  }

  getActivityOptions() {
    return axios.get(`${this.url}/activity_options`);
  }

  getTasks(params = {}) {
    return axios.get(`${this.url}/tasks`, { params });
  }

  createTask(data) {
    return axios.post(`${this.url}/tasks`, { task: data });
  }

  updateTask(taskId, data) {
    return axios.patch(`${this.url}/tasks/${taskId}`, { task: data });
  }

  deleteTask(taskId) {
    return axios.delete(`${this.url}/tasks/${taskId}`);
  }

  getFollowUps(params = {}) {
    return axios.get(`${this.url}/follow_ups`, { params });
  }

  createFollowUp(data) {
    return axios.post(`${this.url}/follow_ups`, { follow_up: data });
  }

  updateFollowUp(followUpId, data) {
    return axios.patch(`${this.url}/follow_ups/${followUpId}`, {
      follow_up: data,
    });
  }

  cancelFollowUp(followUpId) {
    return axios.delete(`${this.url}/follow_ups/${followUpId}`);
  }

  retryFollowUp(followUpId) {
    return axios.post(`${this.url}/follow_ups/${followUpId}/retry_message`);
  }

  getGoals(params = {}) {
    return axios.get(`${this.url}/goals`, { params });
  }

  createGoal(data) {
    return axios.post(`${this.url}/goals`, { goal: data });
  }

  updateGoal(goalId, data) {
    return axios.patch(`${this.url}/goals/${goalId}`, { goal: data });
  }

  deleteGoal(goalId) {
    return axios.delete(`${this.url}/goals/${goalId}`);
  }

  getCommissionRules() {
    return axios.get(`${this.url}/commission_rules`);
  }

  createCommissionRule(data) {
    return axios.post(`${this.url}/commission_rules`, {
      commission_rule: data,
    });
  }

  updateCommissionRule(ruleId, data) {
    return axios.patch(`${this.url}/commission_rules/${ruleId}`, {
      commission_rule: data,
    });
  }

  deleteCommissionRule(ruleId) {
    return axios.delete(`${this.url}/commission_rules/${ruleId}`);
  }

  getCommissions(params = {}) {
    return axios.get(`${this.url}/commissions`, { params });
  }

  updateCommission(commissionId, status) {
    return axios.patch(`${this.url}/commissions/${commissionId}`, {
      commission: { status },
    });
  }

  getAutomationRules() {
    return axios.get(`${this.url}/automation_rules`);
  }

  createAutomationRule(data) {
    return axios.post(`${this.url}/automation_rules`, {
      automation_rule: data,
    });
  }

  updateAutomationRule(ruleId, data) {
    return axios.patch(`${this.url}/automation_rules/${ruleId}`, {
      automation_rule: data,
    });
  }

  deleteAutomationRule(ruleId) {
    return axios.delete(`${this.url}/automation_rules/${ruleId}`);
  }

  testAutomationRule(ruleId) {
    return axios.post(`${this.url}/automation_rules/${ruleId}/test`);
  }

  getAutomationExecutions(params = {}) {
    return axios.get(`${this.url}/automation_executions`, { params });
  }

  getAuditEntries(params = {}) {
    return axios.get(`${this.url}/audit_entries`, { params });
  }

  getCustomFields(params = {}) {
    return axios.get(`${this.url}/custom_fields`, { params });
  }

  createCustomField(data) {
    return axios.post(`${this.url}/custom_fields`, { custom_field: data });
  }

  updateCustomField(fieldId, data) {
    return axios.patch(`${this.url}/custom_fields/${fieldId}`, {
      custom_field: data,
    });
  }

  deleteCustomField(fieldId) {
    return axios.delete(`${this.url}/custom_fields/${fieldId}`);
  }

  createDeal(data) {
    return axios.post(`${this.url}/deals`, { deal: data });
  }

  updateDeal(dealId, data) {
    return axios.patch(`${this.url}/deals/${dealId}`, { deal: data });
  }

  deleteDeal(dealId) {
    return axios.delete(`${this.url}/deals/${dealId}`);
  }
}

export default new CrmAPI();
